/**
 * "New" on the project head: start a document that already knows which project
 * and which client it belongs to.
 *
 * The point is not the click saved. It is the pair that cannot come apart. An
 * operator standing on a project who opens a blank invoice has to name the
 * client and the project again from memory, and the two pickers do not check
 * each other - the server does, afterwards, by refusing a cross-client link with
 * `project_client_mismatch`. Every entry here hands both values over together,
 * so the refusal is unreachable from this path.
 *
 * Three entries, and the two that are missing are missing on purpose:
 *
 * - INVOICE and ESTIMATE navigate to the sales editor with `?clientId=&projectId=`.
 *   `DocEditor` reads both once as its initial state, and its client picker
 *   clears the project whenever the client changes, so a pre-filled project
 *   either still belongs to the pre-filled client or is gone. Credit notes and
 *   proposals are not offered: `hasProject` in sales/config.ts is false for both,
 *   so neither has a column to carry the link.
 * - EXPENSE opens `ExpenseModal` with a preset. That modal LOCKS both fields when
 *   the preset carries a project, which is the same guarantee by a stricter route.
 * - CONTRACT and TICKET are not here. Their create forms take no pre-fill at all
 *   (`NewContractModal` and `NewTicketModal` accept only close/created callbacks),
 *   so an entry would navigate to a form that ignores everything this menu knows
 *   and leaves the operator typing what they clicked to avoid typing. Both records
 *   reach a project through the Link button on their own tab instead, which is a
 *   door that exists rather than one that looks like it does.
 *
 * Each entry is gated on the right its own target needs, not on one right for the
 * menu: selling and booking a cost are different jobs, and a project manager
 * without `manage:crm.sales` should still see the expense. When nothing is
 * permitted the trigger is not drawn at all.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Receipt, Wallet } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import type { Project } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { useCrmUser } from "../../shell/CrmUser";
import { ExpenseModal } from "../expenses/ExpenseModal";

export function NewForProjectMenu({
  project,
  onExpenseSaved,
}: {
  project: Project;
  /** Called after a cost is booked, so the page can refresh its figures and show
   *  the operator where the row landed. */
  onExpenseSaved: () => void;
}) {
  const t = useT();
  const user = useCrmUser();
  const canSell = userCanPlatform(user, "manage:crm.sales");
  const canBook = userCanPlatform(user, "manage:crm.expenses");
  const [addingExpense, setAddingExpense] = useState(false);

  if (!canSell && !canBook) return null;

  return (
    <>
      <Pop
        trigger={(open) => (
          <button type="button" className={`crm-btn crm-btn-dark${open ? " is-on" : ""}`} aria-haspopup="menu" aria-expanded={open}>
            <Plus className="size-4" />
            {t("projects.newDocument")}
          </button>
        )}
      >
        <NewMenuItems
          project={project}
          canSell={canSell}
          canBook={canBook}
          onExpense={() => setAddingExpense(true)}
        />
      </Pop>

      {/* Outside the popover, which unmounts its children the moment it closes -
          the dialog has to outlive the menu that opened it. */}
      {addingExpense ? (
        <ExpenseModal
          expense={null}
          preset={{ clientId: project.clientId, projectId: project.id }}
          onClose={() => setAddingExpense(false)}
          onSaved={() => {
            setAddingExpense(false);
            onExpenseSaved();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The entries themselves, split out only because `useClosePops` has to be called
 * from INSIDE the popover to reach it. A menu item that navigates without closing
 * leaves the popover open over the next page.
 */
function NewMenuItems({
  project,
  canSell,
  canBook,
  onExpense,
}: {
  project: Project;
  canSell: boolean;
  canBook: boolean;
  onExpense: () => void;
}) {
  const t = useT();
  const close = useClosePops();
  const navigate = useNavigate();

  /** Both ids together, always. Half of this pair is what the server refuses. */
  const prefill = `?clientId=${encodeURIComponent(project.clientId)}&projectId=${encodeURIComponent(project.id)}`;

  function go(path: string) {
    close();
    navigate(path);
  }

  return (
    <>
      {canSell ? (
        <>
          <button type="button" className="crm-mi" onClick={() => go(`/sales/invoices/new${prefill}`)}>
            <Receipt className="size-4" />
            {t("projects.newInvoice")}
          </button>
          <button type="button" className="crm-mi" onClick={() => go(`/sales/estimates/new${prefill}`)}>
            <FileText className="size-4" />
            {t("projects.newEstimate")}
          </button>
        </>
      ) : null}
      {canBook ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onExpense();
          }}
        >
          <Wallet className="size-4" />
          {t("projects.newExpense")}
        </button>
      ) : null}
    </>
  );
}
